import React, { useEffect, useRef, useState } from 'react'
import { OmButton, OmDropdownMenu, OmPopover } from '@owma/kit'
import chevronDown24SVG from 'assets/images/chevron-down-24.svg'
import menuSVG from 'assets/images/navi/toolbar-navigation.svg'
import navigateNextSVG from 'assets/images/navigation-next-24.svg'
import navigatePrevSVG from 'assets/images/navigation-prev-24.svg'
import syncSVG from 'assets/images/toolbar-sync-24.svg'
import moment from 'moment'
import CalendarDatePicker from 'pages/todo/components/DatePicker'
import { DatePickerType } from 'pages/todo/components/DatePicker/DatePickerType'
import { useTranslation } from 'react-i18next'
import * as calendarService from '../../modules/CalendarService'
import { getWeekFirstDate, getWeekLastDate } from '../../utils/DateUtils'
import styles from './style.module.scss'

type ViewType = 'month' | 'week' | 'day'

interface CalendarToolbarProps {
  currentDate?: Date
  viewType?: ViewType
  onMenuClick?: () => void
  onNewEventClick?: () => void
  onTodayClick?: () => void
  onPrevClick?: () => void
  onNextClick?: () => void
  onMonthClick?: (date: Date) => void
  onViewChange?: (viewType: ViewType) => void
  isSearching?: boolean
}

const CalendarToolbar = ({
  currentDate = new Date(),
  viewType = 'month',
  onMenuClick = () => {},
  onNewEventClick = () => {},
  onTodayClick = () => {},
  onPrevClick = () => {},
  onNextClick = () => {},
  onMonthClick = () => {},
  onViewChange = () => {},
  isSearching = false,
}: CalendarToolbarProps) => {
  const [isRotating, setIsRotating] = useState(false)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const { t } = useTranslation()

  const rotateTimeExpired = useRef(true)

  const syncListener = (isFetching: boolean) => {
    if (isFetching) {
      setIsRotating(true)
      rotateTimeExpired.current = false

      setTimeout(() => {
        if (!calendarService.isSyncing()) {
          setIsRotating(false)
        }
        rotateTimeExpired.current = true
      }, 1000)
    } else if (rotateTimeExpired.current) {
      setIsRotating(false)
    }
  }

  useEffect(() => {
    calendarService.addSyncListener(syncListener)
    return () => {
      calendarService.removeSyncListener(syncListener)
    }
  }, [])

  const handleSyncClick = () => {
    if (isRotating) return

    setIsRotating(true)
    calendarService.reloadEvents(true)
  }

  const handleDateSelect = (date: Date) => {
    onMonthClick(date)
    setDatePickerOpen(false)
  }

  const handleDatePickerClose = () => {
    setDatePickerOpen(false)
  }

  const mDate = moment(currentDate).startOf('day')
  const localeStr = mDate.localeData().longDateFormat('LL')
  const yIndex = localeStr.indexOf('YYYY')
  const hasSpace = localeStr.indexOf(' ') > -1

  let dateDisplayStr = ''
  if (viewType === 'day') {
    dateDisplayStr = mDate.format('LL')
  } else if (viewType === 'week') {
    dateDisplayStr = mDate.format('YYYY') + ', '
    const startOfWeek = moment(getWeekFirstDate(currentDate))
    const endOfWeek = moment(getWeekLastDate(currentDate))
    dateDisplayStr += startOfWeek.format('MMM Do') + ' - ' + endOfWeek.format('MMM Do')
  } else {
    // month
    if (yIndex === 0) {
      dateDisplayStr =
        (mDate.localeData().ordinal as any)(mDate.year(), 'y') +
        (hasSpace ? ' ' : '') +
        mDate.format('MMMM')
    } else {
      dateDisplayStr = mDate.format('MMMM YYYY')
    }
  }

  const items = [
    {
      key: 'month',
      label: t('calendar.frequency.month'),
    },
    {
      key: 'week',
      label: t('calendar.frequency.week'),
    },
    {
      key: 'day',
      label: t('calendar.frequency.day'),
    },
  ]

  return (
    <div className={styles.container} data-testid="calendar-toolbar">
      <div className={styles.leftGroup}>
        <OmButton
          className={styles.menuBtn}
          variant="text"
          size="sm"
          icon={<img src={menuSVG} alt="menu" />}
          onClick={onMenuClick}
          data-testid="calendar-menu-button"
        />
        <button
          className={styles.newEventButton}
          onClick={onNewEventClick}
          data-testid="calendar-new-event-button"
        >
          {t('calendar.edit.newEvent')}
        </button>
        <button
          className={styles.todayButton}
          onClick={onTodayClick}
          disabled={isSearching}
          data-testid="calendar-today-button"
        >
          {t('calendar.edit.today')}
        </button>
        <div className={styles.navigationGroup} data-testid="calendar-navigation-group">
          <button
            className={styles.navigationButton}
            onClick={onPrevClick}
            disabled={isSearching}
            data-testid="calendar-prev-button"
          >
            <img src={navigatePrevSVG} alt="prev" />
          </button>
          <OmPopover
            open={datePickerOpen}
            onOpenChange={setDatePickerOpen}
            placement="bottomLeft"
            arrow={false}
            bodyStyle={{ padding: 0 }}
            content={
              viewType === 'month' ? (
                <CalendarDatePicker
                  pickerType={DatePickerType.MONTH}
                  value={currentDate}
                  useTodayMenu={false}
                  useDialogStyle={true}
                  onChange={handleDateSelect}
                  onClose={handleDatePickerClose}
                />
              ) : (
                <div className={styles.datePickerContainer}>
                  <CalendarDatePicker
                    pickerType={viewType === 'week' ? DatePickerType.WEEK : DatePickerType.DATE}
                    value={currentDate}
                    useTodayMenu={false}
                    useDialogStyle={true}
                    onChange={handleDateSelect}
                    onClose={handleDatePickerClose}
                  />
                </div>
              )
            }
          >
            <button
              className={styles.dateButton}
              disabled={isSearching}
              data-testid="calendar-current-period-button"
              data-view-type={viewType}
            >
              {dateDisplayStr}
              <img src={chevronDown24SVG} alt="chevron-down" style={{ marginLeft: '8px' }} />
            </button>
          </OmPopover>
          <button
            className={styles.navigationButton}
            onClick={onNextClick}
            disabled={isSearching}
            data-testid="calendar-next-button"
          >
            <img src={navigateNextSVG} alt="next" />
          </button>
        </div>
      </div>
      <div className={styles.rightGroup}>
        <button
          className={styles.iconButton}
          onClick={handleSyncClick}
          disabled={isRotating}
          data-testid="calendar-sync-button"
        >
          <img src={syncSVG} alt="sync" className={isRotating ? styles.rotating : ''} />
        </button>
        <OmDropdownMenu
          menu={{ items, onClick: (info) => onViewChange(info.key as ViewType) }}
          trigger={['click']}
          placement="bottomRight"
          overlayClassName={'contextMenu'}
        >
          <button
            className={styles.viewButton}
            data-testid="calendar-view-button"
            data-view-type={viewType}
          >
            {isSearching
              ? t('common.search')
              : items.find((item) => item.key === viewType)?.label || t('calendar.frequency.month')}
            <img src={chevronDown24SVG} alt="chevron-down" />
          </button>
        </OmDropdownMenu>
      </div>
    </div>
  )
}

export default CalendarToolbar
